/**
 * Google Formへの送信を処理するユーティリティ
 * CORS制限を回避するため、hidden iframe + form submitを使用
 */

// 環境別Google Form設定
const GOOGLE_FORM_CONFIG = {
  production: {
    url: 'https://docs.google.com/forms/d/e/1FAIpQLSfPliGnBAVYl8DOLq1EmynDUihzJfu7q-LYW-aZf0SXGw-9QQ/formResponse',
    entryIds: {
      age: 'entry.350710778',
      gender: 'entry.900082064',
      area: 'entry.530679557',
      xAccount: 'entry.117022592',
      workExperience: 'entry.647830004',
      allowance: 'entry.1537699448',
      moneyManagement: 'entry.1288350265',
      q1: 'entry.2114516372',
      q2: 'entry.477025588',
      q3: 'entry.1232160096',
      q4: 'entry.489504883',
      q5: 'entry.1783180205',
      q6: 'entry.690199578',
      q7: 'entry.1321547143',
      q8: 'entry.1206402245',
    },
  },
  development: {
    url: 'https://docs.google.com/forms/d/e/1FAIpQLSff6HHZxU-UKO77xXG-gO8Zcz2I2xxQD-LuX1gnl4lNPC8Ucg/formResponse',
    entryIds: {
      age: 'entry.2106124052',
      gender: 'entry.1407262755',
      area: 'entry.846005035',
      xAccount: 'entry.1386795470',
      workExperience: 'entry.1067083786',
      allowance: 'entry.1303439900',
      moneyManagement: 'entry.1297179791',
      q1: 'entry.1370061467',
      q2: 'entry.1431583396',
      q3: 'entry.1258548229',
      q4: 'entry.1053376328',
      q5: 'entry.1643573443',
      q6: 'entry.471446235',
      q7: 'entry.2012701841',
      q8: 'entry.995902711',
    },
  },
};

// 環境に応じて設定を切り替え
const currentEnv = process.env.NODE_ENV === 'production' ? 'production' : 'development';
const config = GOOGLE_FORM_CONFIG[currentEnv];

const GOOGLE_FORM_ACTION_URL = config.url;
const ENTRY_IDS = config.entryIds;

// デバッグ用：起動時に使用する環境を出力
console.log(`[Google Form] Environment: ${currentEnv}`);
console.log(`[Google Form] URL: ${GOOGLE_FORM_ACTION_URL}`);

/**
 * フォームデータをGoogle Form送信フォーマットに変換
 * @param {Object} formData - アンケートフォームデータ
 * @returns {Object} Google Form Entry ID形式のデータ
 */
function convertToGoogleFormData(formData) {
  const googleFormData = {};

  // プロフィールデータ
  googleFormData[ENTRY_IDS.age] = formData.profile.age || '';
  googleFormData[ENTRY_IDS.gender] = formData.profile.gender || '';
  googleFormData[ENTRY_IDS.area] = ''; // 居住エリア: 常に空文字
  googleFormData[ENTRY_IDS.xAccount] = formData.profile.contact || ''; // 連絡先（旧Xアカウント）

  // 削除された項目: 常に空文字を送信
  googleFormData[ENTRY_IDS.workExperience] = ''; // アルバイト
  googleFormData[ENTRY_IDS.allowance] = ''; // お小遣い
  googleFormData[ENTRY_IDS.moneyManagement] = formData.moneyManagement || '';

  // 質問3（旧Q1）: 100万円の使い道（単一選択+その他）
  if (formData.questions.q3 === 'その他' && formData.questions.q3Other) {
    googleFormData[ENTRY_IDS.q1] = `その他: ${formData.questions.q3Other}`;
  } else {
    googleFormData[ENTRY_IDS.q1] = formData.questions.q3 || '';
  }

  // 削除された質問2（将来の稼ぎ方）: 常に空文字
  googleFormData[ENTRY_IDS.q2] = '';

  // 質問4（旧Q3）: 推しキャラクター（単一選択）
  googleFormData[ENTRY_IDS.q3] = formData.questions.q4 || '';

  // 質問5（旧Q4）: 視聴動画（複数選択、任意）
  googleFormData[ENTRY_IDS.q4] = Array.isArray(formData.questions.q5)
    ? formData.questions.q5.join(', ')
    : '';

  // 質問6（旧Q5）: 視聴きっかけ（複数選択+その他、任意）
  if (Array.isArray(formData.questions.q6) && formData.questions.q6.length > 0) {
    const values = [...formData.questions.q6];
    const otherIndex = values.indexOf('その他');
    if (otherIndex !== -1 && formData.questions.q6Other) {
      values[otherIndex] = `その他: ${formData.questions.q6Other}`;
    }
    googleFormData[ENTRY_IDS.q5] = values.join(', ');
  } else {
    googleFormData[ENTRY_IDS.q5] = '';
  }

  // 質問7（旧Q6）: お金の意識変化（単一選択、任意）
  googleFormData[ENTRY_IDS.q6] = formData.questions.q7 || '';

  // 質問8（旧Q7）: 実践したいこと（複数選択+その他、任意）
  if (Array.isArray(formData.questions.q8) && formData.questions.q8.length > 0) {
    const values = [...formData.questions.q8];
    const otherIndex = values.indexOf('その他');
    if (otherIndex !== -1 && formData.questions.q8Other) {
      values[otherIndex] = `その他: ${formData.questions.q8Other}`;
    }
    googleFormData[ENTRY_IDS.q7] = values.join(', ');
  } else {
    googleFormData[ENTRY_IDS.q7] = '';
  }

  // 質問9（旧Q8）: Tシャツサイズ（複数選択、任意）
  googleFormData[ENTRY_IDS.q8] = Array.isArray(formData.questions.q9)
    ? formData.questions.q9.join(', ')
    : '';

  return googleFormData;
}

/**
 * hidden iframeを作成
 * @returns {HTMLIFrameElement}
 */
function createHiddenIframe() {
  const iframe = document.createElement('iframe');
  iframe.name = 'google-form-target';
  iframe.style.display = 'none';
  document.body.appendChild(iframe);
  return iframe;
}

/**
 * Google Form送信用のformを作成
 * @param {Object} googleFormData - Google Form Entry ID形式のデータ
 * @returns {HTMLFormElement}
 */
function createSubmissionForm(googleFormData) {
  const form = document.createElement('form');
  form.setAttribute('method', 'POST');
  form.setAttribute('action', GOOGLE_FORM_ACTION_URL);
  form.setAttribute('target', 'google-form-target');
  form.setAttribute('data-google-form', 'true');

  // 各フィールドをhidden inputとして追加
  Object.entries(googleFormData).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  return form;
}

/**
 * DOM要素をクリーンアップ
 * @param {HTMLFormElement} form
 * @param {HTMLIFrameElement} iframe
 */
function cleanupElements(form, iframe) {
  if (form && form.parentNode) {
    form.parentNode.removeChild(form);
  }
  if (iframe && iframe.parentNode) {
    iframe.parentNode.removeChild(iframe);
  }
}

/**
 * Google Formにデータを送信
 * @param {Object} formData - アンケートフォームデータ
 * @returns {Promise<Object>} 送信結果
 */
async function submitToGoogleForm(formData) {
  return new Promise((resolve, reject) => {
    try {
      // 1. データを変換
      const googleFormData = convertToGoogleFormData(formData);

      // 2. hidden iframeを作成
      const iframe = createHiddenIframe();

      // 3. formを作成
      const form = createSubmissionForm(googleFormData);

      // 4. formを送信
      form.submit();

      // 5. 送信後のクリーンアップ（2秒後）
      setTimeout(() => {
        cleanupElements(form, iframe);
        resolve({
          success: true,
          timestamp: new Date(),
        });
      }, 2000);
    } catch (error) {
      const err = new Error(error.message);
      err.success = false;
      err.timestamp = new Date();
      reject(err);
    }
  });
}

export default {
  convertToGoogleFormData,
  submitToGoogleForm,
};
